import autobind from 'class-autobind';

import FactorReduction from 'atg/api/FactorReduction';
import * as device from 'atg/utils/device';
import * as Utils from 'atg/utils/Utils';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import BaseView from './BaseView';

export default class FactorReductionView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, FactorReductionView.prototype);
  }

  getGridClass() {
    return 'view-factor-reduction';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get('view-factor', this.getNumber(FactorReductionView));
  }

  getMenuTitle() {
    return Text.get('view-factor', this.getNumber(FactorReductionView));
  }

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  getMenuSelected() {
    const betInformation = this.getAPI().betInformation;
    const numberOfRows = betInformation.getNumberOfRows();

    for (let row = 0; row < numberOfRows; row++) {
      const betRow = betInformation.getRow(row);

      if (betRow.hasAnyFactors()) {
        return true;
      }
    }

    return false;
  }

  // ---------------------------------------- GRID ----------------------------------------
  onRowWasUpdated() {}
  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let betConfig;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      betConfig = rowCache.betConfig;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};

      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;

    const start = race.getStart(startIndex);

    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    if (betRow) {
      info.bottomBlue = betRow.getFactorReductionFactor(startIndex);
    }

    return info;
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const cell = this.getGrid().getCellByPosition(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
      {
        icon: 'button-minus',
        onPress: this.onGridMinusButtonPressed,
      },
      {
        icon: 'button-plus',
        onPress: this.onGridPlusButtonPressed,
      },
      {
        icon: 'button-numpad',
        onPress: this.onGridNumpadButtonPressed,
      },
      ...this.makeHorseButtons(position),
    ];

    if (
      cell.hasClass('in-range') &&
      !device.isTouch() &&
      this.getGuaranteeValue() === 0
    ) {
      this.onGridNumpadButtonPressed(position);
    }

    this.getGrid().showCellButtons(position, buttons);
  }

  onGridPlusButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();
    const willShowGuaranteeAlert = this.showAlertWhenGuaranteeSet();

    if (willShowLockedAlert || willShowGuaranteeAlert) {
      this.getGrid().closeHighlight();
    } else {
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const startNumber = betRow.getStartNumberAtPosition(position.column);
      const startIndex = startNumber - 1;
      const factor = betRow.getFactorReductionFactor(startIndex);

      betRow.setFactorReductionFactor(startIndex, Math.min(10, factor + 1));
      this.getGrid().updateRow(position.row);
      this.getAPI().scheduleBetUpdate();
    }
  }

  onGridMinusButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();
    const willShowGuaranteeAlert = this.showAlertWhenGuaranteeSet();

    if (willShowLockedAlert || willShowGuaranteeAlert) {
      this.getGrid().closeHighlight();
    } else {
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const startNumber = betRow.getStartNumberAtPosition(position.column);
      const startIndex = startNumber - 1;
      const factor = betRow.getFactorReductionFactor(startIndex);

      betRow.setFactorReductionFactor(startIndex, Math.max(1, factor - 1));
      this.getGrid().updateRow(position.row);

      this.getAPI().scheduleBetUpdate();
    }
  }

  onGridNumpadButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();
    const willShowGuaranteeAlert = this.showAlertWhenGuaranteeSet();

    if (willShowLockedAlert || willShowGuaranteeAlert) {
      this.getGrid().closeHighlight();
    } else {
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const startNumber = betRow.getStartNumberAtPosition(position.column);
      const startIndex = startNumber - 1;
      const factor = betRow.getFactorReductionFactor(startIndex);

      this.getGrid().showNumberInput(position, factor);
    }
  }

  // ---------------------------------------- DATA ----------------------------------------

  getItem() {
    return this.getFactorReduction();
  }

  getFactorReduction() {
    let reduction = this.getAPI()
      .betInformation.getReductionSet()
      .getFactorReduction();

    if (!reduction) {
      reduction = new FactorReduction();
    }

    return reduction;
  }

  deleteItem() {
    const api = this.getAPI();

    const numberOfRows = api.betInformation.getNumberOfRows();
    for (let row = 0; row < numberOfRows; row++) {
      api.betInformation.getRow(row).deleteFactorReduction();
    }

    super.deleteItem();
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoHidden() {
    return true;
  }

  getBottomInfoContent() {
    return '';
  }

  onGridNumberInputChanged(position, factor) {
    let clampedFactor = Math.max(1, factor);
    clampedFactor = Math.min(10, clampedFactor);

    const betRow = this.getAPI().betInformation.getRow(position.row);
    const startNumber = betRow.getStartNumberAtPosition(position.column);
    const startIndex = startNumber - 1;

    betRow.setFactorReductionFactor(startIndex, clampedFactor);

    Utils.debug(position, startIndex, clampedFactor);

    this.getAPI().scheduleBetUpdate();
  }
}
